import { Fetcher } from '@this/src/util';
import React, { useMemo, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { reportError } from '@this/lib/bugsnag';
import { Button } from '@this/shared/ui/inputs/button';
import { Link } from '@this/shared/ui/navigations/link';
import { TextField } from '../../shared/form_elements/form_elements';
import { isIncludeHyphen } from '../../shared/checker/hyphen_checker';

import type User from '../../../domain/user/user';

const PASSWORD_MIN_LENGTH = 10;

interface Props {
  user: User | null | undefined;
}

interface FormErrors {
  password?: string;
  last_name?: string;
  first_name?: string;
  tel?: string;
}

interface ErrorResponse {
  response?: {
    status?: number;
    data?: {
      errors?: FormErrors;
    };
  };
}

const Reapplication = ({ user }: Props) => {
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [tel, setTel] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);
  const styles = useMemo(() => getStyles(), []);

  const handleTelChange = (value: string) => {
    setErrors(prev => ({
      ...prev,
      tel: isIncludeHyphen(value) ? 'ハイフンを含めず入力してください' : undefined
    }));
    setTel(value);
  };

  const validationErrors = (): FormErrors => {
    const errs: FormErrors = {};
    if (!lastName) errs.last_name = '姓を入力してください';
    if (!firstName) errs.first_name = '名を入力してください';
    if (!password) {
      errs.password = 'パスワードを入力してください';
    } else if (password.length < PASSWORD_MIN_LENGTH) {
      errs.password = `パスワードは${PASSWORD_MIN_LENGTH}文字以上で入力してください`;
    }
    if (!tel) {
      errs.tel = '電話番号を入力してください';
    } else if (isIncludeHyphen(tel)) {
      errs.tel = 'ハイフンを含めず入力してください';
    }
    return errs;
  };

  const confirmToken = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const token = utils.getParam('reapplication_token');
    const nextErrors = validationErrors();
    if (!token) {
      setFailed(true);
      return;
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    setErrors({});
    setSubmitError(null);
    try {
      await Fetcher.post('/aiexpense/users/reapplication.json', {
        reapplication_token: token,
        last_name: lastName,
        first_name: firstName,
        tel,
        password
      });
      setDone(true);
    } catch (error) {
      const response = (error as ErrorResponse).response;
      if (response?.status === 400) {
        setErrors(response.data?.errors ?? {});
      } else if (response?.status === 404) {
        setFailed(true);
      } else {
        setSubmitError('通信エラーが発生しました。時間をおいて再度お試しください。');
      }
    } finally {
      setSubmitting(false);
    }
  };

  try {
    if (user) {
      return <Redirect to="/aiexpense/report_items" />;
    }

    return (
      <div className="expenses-users-signup" style={styles.container}>
        <div style={styles.content}>
          {done ? (
            <>
              <div style={styles.title}>再お申し込みが完了しました</div>
              <p style={styles.message}>入力したパスワードで、下記リンクからログインできます</p>
              <Link href="/aiexpense/users/sign_in">ログインして始める</Link>
            </>
          ) : failed ? (
            <>
              <div style={styles.title}>リンクの有効期限が切れています</div>
              <p style={styles.message}>下記のリンクから再度お申し込みを行ってください。</p>
              <Link href="/aiexpense/users/sign_up">お申し込みページ</Link>
            </>
          ) : (
            <form onSubmit={confirmToken}>
              <div style={styles.title}>再お申し込みの確認</div>
              <p style={styles.message}>氏名・電話番号・パスワードを入力すると、新しい組織が作成されます。</p>
              <div style={styles.nameFieldRow}>
                <div style={styles.nameFieldFirst}>
                  <TextField
                    label="姓（必須）"
                    value={lastName}
                    example="例：山田"
                    error={errors.last_name}
                    onChange={setLastName}
                  />
                </div>
                <div style={styles.nameField}>
                  <TextField
                    label="名（必須）"
                    value={firstName}
                    example="例：一郎"
                    error={errors.first_name}
                    onChange={setFirstName}
                  />
                </div>
              </div>
              <TextField
                label="電話番号（必須）"
                value={tel}
                example="例：0312345678"
                error={errors.tel}
                onChange={handleTelChange}
              />
              <TextField
                label="パスワード（必須）"
                value={password}
                example=""
                type="password"
                error={errors.password}
                onChange={setPassword}
              />
              <div style={styles.submit}>
                <Button type="submit" loading={submitting} disabled={submitting}>
                  再お申し込みを完了する
                </Button>
              </div>
              {submitError && <div className="error">{submitError}</div>}
            </form>
          )}
        </div>
      </div>
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

const getStyles = () => ({
  container: {
    maxWidth: '600px',
    width: '100%',
    margin: '80px auto'
  },
  content: {
    padding: '40px',
    background: 'white',
    borderRadius: '8px',
    fontSize: '16px'
  },
  title: {
    fontSize: '22px',
    fontWeight: 'bold' as const,
    marginBottom: '16px',
    textAlign: 'center' as const
  },
  message: {
    color: '#555',
    fontWeight: 'normal' as const,
    marginBottom: '32px',
    lineHeight: '1.6',
    textAlign: 'center' as const
  },
  nameFieldRow: {
    display: 'flex',
    alignItems: 'center' as const,
    marginBottom: '40px'
  },
  nameFieldFirst: {
    flexGrow: 999,
    marginRight: '20px'
  },
  nameField: {
    flexGrow: 999
  },
  submit: {
    textAlign: 'center' as const
  }
});

export default Reapplication;
